import React, { useState, useEffect } from 'react';
import { taxCertificatesAPI } from '../../services/portalAPI';
import { API_HOST } from '../../services/api';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import PortalFooter from '../../components/Layout/PortalFooter';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import '../../styles/portal-brand.css';
import './TaxExemption.css';

// Official WA DOR Form 27-0036 URL
const WA_FORM_URL = 'https://dor.wa.gov/sites/default/files/2022-02/27-0036.pdf';

interface TaxCertificate {
  id: number;
  customer: number;
  customer_name: string;
  certificate_file: string;
  original_filename: string;
  certificate_number?: string;
  issuing_state: string;
  issue_date?: string;
  expiration_date: string;
  status: 'ACTIVE' | 'EXPIRING_SOON' | 'EXPIRED' | 'SUPERSEDED';
  uploaded_at: string;
  uploaded_by?: number;
  uploaded_by_name?: string;
  notes?: string;
  is_expired: boolean;
  days_until_expiration: number;
  is_expiring_soon: boolean;
}

const TaxExemption: React.FC = () => {
  const { user } = usePortalAuth();
  const [certificates, setCertificates] = useState<TaxCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    certificate_file: null as File | null,
    certificate_number: '',
    issuing_state: 'WA',
    issue_date: '',
    expiration_date: '',
    notes: '',
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const response = await taxCertificatesAPI.getAll();
      // Handle various API response formats
      const data = response.data;
      let certList: TaxCertificate[] = [];

      if (Array.isArray(data)) {
        certList = data;
      } else if (data?.results && Array.isArray(data.results)) {
        certList = data.results;
      } else if (data && typeof data === 'object') {
        // If it's a single object, wrap in array
        certList = [data];
      }

      setCertificates(certList);
    } catch (err: any) {
      setError('Failed to load tax certificates');
      console.error(err);
      setCertificates([]); // Ensure we have an empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadForm({ ...uploadForm, certificate_file: e.target.files[0] });
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!uploadForm.certificate_file) {
      alert('Please select a certificate file');
      return;
    }

    if (!uploadForm.expiration_date) {
      alert('Please enter an expiration date');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('certificate_file', uploadForm.certificate_file);
      formData.append('original_filename', uploadForm.certificate_file.name);
      formData.append('expiration_date', uploadForm.expiration_date);
      formData.append('issuing_state', uploadForm.issuing_state);

      if (uploadForm.certificate_number) {
        formData.append('certificate_number', uploadForm.certificate_number);
      }
      if (uploadForm.issue_date) {
        formData.append('issue_date', uploadForm.issue_date);
      }
      if (uploadForm.notes) {
        formData.append('notes', uploadForm.notes);
      }

      // Add customer ID from user context
      if (user?.customer?.id) {
        formData.append('customer', user.customer.id.toString());
      }

      await taxCertificatesAPI.upload(formData);

      // Reset form and reload certificates
      setUploadForm({
        certificate_file: null,
        certificate_number: '',
        issuing_state: 'WA',
        issue_date: '',
        expiration_date: '',
        notes: '',
      });
      setShowUploadModal(false);
      loadCertificates();
      alert('Certificate uploaded successfully!');
    } catch (err: any) {
      console.error('Upload failed:', err);
      alert('Failed to upload certificate. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (cert: TaxCertificate) => {
    try {
      const response = await taxCertificatesAPI.download(cert.id);
      const url = response.data.url;
      // URL may be absolute (Spaces presigned URL) or relative
      if (url.startsWith('http')) {
        window.open(url, '_blank');
      } else {
        window.open(`${API_HOST}${url}`, '_blank');
      }
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download certificate');
    }
  };

  const getStatusBadgeClass = (cert: TaxCertificate) => {
    // If days are negative, it's expired regardless of backend status
    if (cert.days_until_expiration < 0) {
      return 'status-badge status-expired';
    }
    switch (cert.status) {
      case 'ACTIVE':
        return 'status-badge status-active';
      case 'EXPIRING_SOON':
        return 'status-badge status-expiring';
      case 'EXPIRED':
        return 'status-badge status-expired';
      case 'SUPERSEDED':
        return 'status-badge status-superseded';
      default:
        return 'status-badge';
    }
  };

  const getStatusText = (cert: TaxCertificate) => {
    // If days are negative, it's expired regardless of backend status
    if (cert.days_until_expiration < 0) {
      return 'Expired';
    }
    switch (cert.status) {
      case 'ACTIVE':
        return 'Active';
      case 'EXPIRING_SOON':
        return 'Expiring Soon';
      case 'EXPIRED':
        return 'Expired';
      case 'SUPERSEDED':
        return 'Superseded';
      default:
        return cert.status;
    }
  };

  const isExpired = (cert: TaxCertificate) => {
    return cert.days_until_expiration < 0 || cert.status === 'EXPIRED';
  };

  const isExpiringSoon = (cert: TaxCertificate) => {
    return !isExpired(cert) && (cert.status === 'EXPIRING_SOON' || cert.days_until_expiration <= 30);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Safely find active certificate (certificates is always an array now)
  const activeCertificate = certificates?.find(cert => cert.status === 'ACTIVE' || cert.status === 'EXPIRING_SOON');

  if (loading) {
    return (
      <div className="portal-page">
        <div className="portal-container">
          <div className="loading">Loading certificates...</div>
        </div>
        <PortalFooter />
      </div>
    );
  }

  return (
    <div className="portal-page">
      <TutorialAutoStart pageKey="tax-exemption" />
      <div className="portal-container tax-exemption-page">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>Tax Exemption Certificates</h1>
            <p className="page-subtitle">Manage your tax exemption certificates</p>
          </div>
          <TutorialHelpButton pageKey="tax-exemption" />
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {/* Active Certificate Section */}
        {activeCertificate && (
          <div className="active-certificate-section" data-tutorial="active-cert">
            <h2>Current Tax Exemption Certificate</h2>
            <div className="certificate-card active-certificate">
              <div className="certificate-header">
                <div>
                  <h3>{activeCertificate.certificate_number || 'Certificate #' + activeCertificate.id}</h3>
                  <span className={getStatusBadgeClass(activeCertificate)}>
                    {getStatusText(activeCertificate)}
                  </span>
                </div>
                <button
                  onClick={() => handleDownload(activeCertificate)}
                  className="btn btn-secondary"
                >
                  Download Certificate
                </button>
              </div>

              <div className="certificate-details">
                <div className="detail-row">
                  <strong>Issuing State:</strong>
                  <span>{activeCertificate.issuing_state}</span>
                </div>
                <div className="detail-row">
                  <strong>Expiration Date:</strong>
                  <span>{formatDate(activeCertificate.expiration_date)}</span>
                </div>
                {activeCertificate.issue_date && (
                  <div className="detail-row">
                    <strong>Issue Date:</strong>
                    <span>{formatDate(activeCertificate.issue_date)}</span>
                  </div>
                )}
                <div className="detail-row">
                  <strong>{activeCertificate.days_until_expiration < 0 ? 'Days Since Expiration:' : 'Days Until Expiration:'}</strong>
                  <span className={isExpired(activeCertificate) ? 'text-danger' : isExpiringSoon(activeCertificate) ? 'text-warning' : ''}>
                    {activeCertificate.days_until_expiration < 0
                      ? `${Math.abs(activeCertificate.days_until_expiration)} days ago`
                      : `${activeCertificate.days_until_expiration} days`}
                  </span>
                </div>
                {activeCertificate.notes && (
                  <div className="detail-row">
                    <strong>Notes:</strong>
                    <span>{activeCertificate.notes}</span>
                  </div>
                )}
              </div>

              {isExpired(activeCertificate) && (
                <div className="alert alert-danger">
                  <strong>Certificate Expired!</strong> Your tax exemption certificate has expired. Please upload a new certificate immediately to maintain your tax-exempt status.
                </div>
              )}
              {isExpiringSoon(activeCertificate) && (
                <div className="alert alert-warning">
                  <strong>Expiring Soon!</strong> Your tax exemption certificate is expiring soon. Please upload a new certificate to maintain your tax-exempt status.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Download & Upload Section */}
        <div className="upload-section">
          <div className="download-form-card">
            <h3>Download Official WA Form</h3>
            <p>Download the WA State Farmer's Certificate (Form 27-0036), fill it out, and upload it below.</p>
            <a
              href={WA_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Download Form (PDF)
            </a>
          </div>

          <div className="upload-form-card">
            <h3>Upload Your Certificate</h3>
            <p>Once you've filled out the form, upload it here for our records.</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="btn btn-secondary"
              data-tutorial="upload-cert"
            >
              Upload Certificate
            </button>
          </div>
        </div>

        {/* Certificate History */}
        <div className="certificate-history" data-tutorial="cert-history">
          <h2>Certificate History</h2>
          {certificates.length === 0 ? (
            <div className="no-certificates">
              <p>No certificates found.</p>
              <p className="text-muted">Upload your first tax exemption certificate to get started.</p>
            </div>
          ) : (
            <div className="certificates-list">
              {certificates.map((cert) => (
                <div key={cert.id} className="certificate-card">
                  <div className="certificate-header-small">
                    <div>
                      <strong>{cert.certificate_number || 'Certificate #' + cert.id}</strong>
                      <span className={getStatusBadgeClass(cert)}>
                        {getStatusText(cert)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDownload(cert)}
                      className="btn-link"
                    >
                      Download
                    </button>
                  </div>
                  <div className="certificate-meta">
                    <span>State: {cert.issuing_state}</span>
                    <span>Expires: {formatDate(cert.expiration_date)}</span>
                    <span>Uploaded: {formatDate(cert.uploaded_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="modal-overlay" onClick={() => !uploading && setShowUploadModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Upload Tax Exemption Certificate</h2>
                <button
                  className="modal-close"
                  onClick={() => !uploading && setShowUploadModal(false)}
                  disabled={uploading}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleUpload} className="upload-form">
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="certificate_file">Certificate File *</label>
                    <input
                      type="file"
                      id="certificate_file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                      required
                      disabled={uploading}
                    />
                    <small className="form-text">Accepted formats: PDF, JPG, PNG (Max 10MB)</small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="certificate_number">Certificate Number</label>
                      <input
                        type="text"
                        id="certificate_number"
                        value={uploadForm.certificate_number}
                        onChange={(e) => setUploadForm({ ...uploadForm, certificate_number: e.target.value })}
                        placeholder="Optional"
                        disabled={uploading}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="issuing_state">Issuing State *</label>
                      <select
                        id="issuing_state"
                        value={uploadForm.issuing_state}
                        onChange={(e) => setUploadForm({ ...uploadForm, issuing_state: e.target.value })}
                        required
                        disabled={uploading}
                      >
                        <option value="WA">Washington</option>
                        <option value="OR">Oregon</option>
                        <option value="CA">California</option>
                        <option value="ID">Idaho</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="issue_date">Issue Date</label>
                      <input
                        type="date"
                        id="issue_date"
                        value={uploadForm.issue_date}
                        onChange={(e) => setUploadForm({ ...uploadForm, issue_date: e.target.value })}
                        disabled={uploading}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="expiration_date">Expiration Date *</label>
                      <input
                        type="date"
                        id="expiration_date"
                        value={uploadForm.expiration_date}
                        onChange={(e) => setUploadForm({ ...uploadForm, expiration_date: e.target.value })}
                        required
                        disabled={uploading}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="notes">Notes</label>
                    <textarea
                      id="notes"
                      value={uploadForm.notes}
                      onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                      rows={3}
                      placeholder="Any additional notes or comments"
                      disabled={uploading}
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="btn btn-secondary"
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload Certificate'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>

      <PortalFooter />
    </div>
  );
};

export default TaxExemption;
